import React, { useContext, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { CheckRounded, ExpandMoreRounded, ExpandLessRounded } from '@material-ui/icons'
import clsx from 'clsx'

import { context, useSelectors } from '../../../../store'
import { applyServiceSlug, SLUG_TO_SERVICE_NAME } from '../../../../lib/apply-service-slug'
import { AnalyticsService } from '../../../../services/analytics'
import { browserHistory } from '../../../../services/history'
import { WASH_DESCRIPTORS } from '../../../../lib/wash-copy'
import { LEAD_WASH_NAMES } from '../../../../lib/wash-order'
import { largeCarLine } from '../../../../lib/price-lines'
import { LARGE_CAR_SERVICE } from '../../../../store/constants/prices'

import { styles } from './styles'

// Once per module: makeStyles called inside a render built a new JSS sheet,
// with new class names, on every render (booking screen, 11 Oct 2026).
const useStyles = makeStyles(styles)

// The wash picker on the Book & pay screen. Three washes are 89% of paid
// orders (prod, last 180 days) so they're the cards; the rest sit behind
// "See all washes". Selecting dispatches the same base-service switch the
// /services tiles use (applyServiceSlug), so includes/excludes stay right.
// Exterior Wash & Wax, Mini Valet, Mini Valet PLUS (Robin, 25 Oct 2026;
// lib/wash-order.js); the rest in the catalogue's order.
const FEATURED = LEAD_WASH_NAMES
const NAME_TO_SLUG = Object.fromEntries(Object.entries(SLUG_TO_SERVICE_NAME).map(([slug, name]) => [name, slug]))

// Set by "Compare all washes", taken once by Book & pay when it is back: the
// visitor returns to the wash they were choosing, not to wherever the browser
// restores the scroll (the open picker's foot, above which it now closes).
let comparing = false
export const takeCompareReturn = () => {
  const was = comparing
  comparing = false
  return was
}

export const WashPicker = React.memo(({ compact = false, onPicked }) => {
  const classes = useStyles()
  const { dispatch } = useContext(context)
  const { prices, services, baseService } = useSelectors()
  const [showAll, setShowAll] = useState(false)

  const bases = useMemo(() => prices.ids.map(id => prices.byId[id]).filter(s => s && s.group === 'base'), [prices])
  const selectedId = useMemo(() => bases.find(s => services[s.id])?.id ?? null, [bases, services])
  const featured = useMemo(() => FEATURED.map(n => bases.find(s => s.name === n)).filter(Boolean), [bases])
  const rest = useMemo(() => bases.filter(s => !featured.includes(s)), [bases, featured])
  // A non-featured wash that is already selected (e.g. from a /services tile)
  // shows in the visible row so the selection is never hidden.
  const visible = useMemo(() => {
    const hidden = rest.find(s => s.id === selectedId)
    return hidden ? [...featured, hidden] : featured
  }, [featured, rest, selectedId])
  const more = useMemo(() => rest.filter(s => !visible.includes(s)), [rest, visible])

  if (bases.length === 0) return null

  const pick = s => {
    const slug = NAME_TO_SLUG[s.name]
    if (slug) applyServiceSlug(slug, { prices, services, dispatch })
    AnalyticsService.trackBookingEntry('book_and_pay_picker', { service_slug: slug })
    onPicked && onPicked(s)
  }

  // Each card leads with what that wash adds over the others (Robin, 5 Oct),
  // from the same lines the home page's wash cards use (lib/wash-copy.js).
  // A wash with no line there keeps its database description.
  const card = s => {
    const on = s.id === selectedId
    const description = WASH_DESCRIPTORS[NAME_TO_SLUG[s.name]] || s.description
    // "Large car +£20" on every card, before the size is asked (19 Oct 2026,
    // docs/DECISIONS.md).
    const large = largeCarLine(LARGE_CAR_SERVICE.customerPrice)
    return (
      <button type="button" key={s.id} className={clsx(classes.card, on && classes.cardOn, compact && classes.cardCompact)} onClick={() => pick(s)} aria-pressed={on}>
        <span className={clsx(classes.tick, on && classes.tickOn)}>{on && <CheckRounded style={{ fontSize: 14 }} />}</span>
        <span className={classes.body}>
          <span className={classes.name}>{s.name}</span>
          {!compact && description && <span className={classes.desc}>{description}</span>}
          {large && <span className={classes.large}>{large}</span>}
        </span>
        <span className={classes.price}>{s.customerPrice}</span>
      </button>
    )
  }

  return (
    <div className={classes.root} data-testid="wash-picker">
      <div className={classes.cards}>{visible.map(card)}</div>
      {more.length > 0 && (
        <button type="button" className={classes.moreLink} onClick={() => setShowAll(v => !v)}>
          {showAll ? <ExpandLessRounded fontSize="small" /> : <ExpandMoreRounded fontSize="small" />}
          {showAll ? 'Fewer washes' : `See all washes (${more.length} more)`}
        </button>
      )}
      {showAll && <div className={classes.cards}>{more.map(card)}</div>}
      {/* The table of all six, inside the app so Back returns to this form as
          it was (the store, and the draft behind it; Robin, 25 Oct 2026). */}
      <a
        className={classes.moreLink}
        href="/services#compare"
        onClick={e => {
          if (e.metaKey || e.ctrlKey || e.shiftKey) return
          e.preventDefault()
          comparing = true
          browserHistory.push({ pathname: '/services', hash: '#compare' })
        }}>
        Compare all washes
      </a>
    </div>
  )
})
